import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthGuard } from './components/AuthGuard';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { CasesList } from './pages/CasesList';
import { CaseDetail } from './pages/CaseDetail';
import { NewCase } from './pages/NewCase';
import { AndyProWorkflow } from './pages/AndyProWorkflow';
import { ToastContainer } from './components/ui';
import { useToastStore } from './stores/toast-store';
import {
  ValidationLayout,
  ValidationDashboard,
  TestCaseList,
  TestCaseWizard,
  TestCaseDetail,
  TestRunList,
  TestRunCreate,
  TestRunDetail,
  BlindEvaluation,
  SearchBlindEvaluation,
  StabilityBlindEvaluation,
  SearchStabilityEvaluation,
  TestRunResults,
  StabilityTestResults,
  EvaluationList,
  AnalyticsDashboard,
  GrandRoundsView,
  QualityAssessment,
  ProductionWorkflow,
} from './pages/validation';

// Wrapper to provide unique key for AndyProWorkflow based on route
function AndyProWorkflowWithKey() {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  // Use case ID + search params as key to ensure fresh state on navigation
  const key = id ? `case-${id}-${location.search}` : `standalone-${location.search}`;
  return <AndyProWorkflow key={key} />;
}

function App() {
  const { toasts, dismissToast } = useToastStore();

  return (
    <AuthGuard>
      <Routes>
        {/* Main app routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="cases" element={<CasesList />} />
          <Route path="cases/new" element={<NewCase />} />
          <Route path="cases/:id" element={<CaseDetail />} />
          <Route path="cases/:id/andy-pro" element={<AndyProWorkflowWithKey />} />
          {/* Standalone Andy Pro workflow - no existing case required */}
          <Route path="andy-pro" element={<AndyProWorkflowWithKey />} />
        </Route>

        {/* Validation routes - separate layout */}
        <Route path="/validation" element={<ValidationLayout />}>
          <Route index element={<ValidationDashboard />} />
          <Route path="test-cases" element={<TestCaseList />} />
          <Route path="test-cases/new" element={<TestCaseWizard />} />
          <Route path="test-cases/:id" element={<TestCaseDetail />} />
          <Route path="test-cases/:id/edit" element={<TestCaseWizard />} />
          <Route path="test-runs" element={<TestRunList />} />
          <Route path="test-runs/new" element={<TestRunCreate />} />
          <Route path="test-runs/:id/edit" element={<TestRunCreate />} />
          <Route path="test-runs/:id" element={<TestRunDetail />} />
          <Route path="test-runs/:id/evaluate" element={<BlindEvaluation />} />
          <Route path="test-runs/:id/search-evaluate" element={<SearchBlindEvaluation />} />
          <Route path="test-runs/:id/stability-evaluate" element={<StabilityBlindEvaluation />} />
          <Route path="test-runs/:id/search-stability-evaluate" element={<SearchStabilityEvaluation />} />
          <Route path="test-runs/:id/quality-assessment" element={<QualityAssessment />} />
          <Route path="test-runs/:id/results" element={<TestRunResults />} />
          <Route path="test-runs/:id/stability-results" element={<StabilityTestResults />} />
          <Route path="grand-rounds/:id" element={<GrandRoundsView />} />
          <Route path="evaluations" element={<EvaluationList />} />
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="production-workflow" element={<ProductionWorkflow />} />
        </Route>

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </AuthGuard>
  );
}

export default App;
